import type { ReportSprintScope, ReportSprintTotals, RetrospectiveEntry, RetrospectiveListKind, SprintRetrospective } from "../../shared/api/types";
import { formatDate } from "../../shared/lib/format";
import { retrospectiveText } from "./retrospectiveText";

export const LISTS: readonly { kind: RetrospectiveListKind; title: () => string }[] = [
  { kind: "went_well", title: () => retrospectiveText("wentWell") },
  { kind: "to_improve", title: () => retrospectiveText("toImprove") },
  { kind: "actions", title: () => retrospectiveText("actions") },
];

/** §9.4: entries of one list, in creation order (the server sends every list in creation order). */
export function entriesOf(retro: SprintRetrospective, kind: RetrospectiveListKind): RetrospectiveEntry[] {
  return retro.entries.filter((entry) => entry.list === kind);
}

function number(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(1);
}

export function totalsText(totals: ReportSprintTotals): string {
  const key = totals.items === 1 ? (totals.points === 1 ? "itemPoint" : "itemPoints") : totals.points === 1 ? "itemsPoint" : "itemsPoints";
  return retrospectiveText(key, { items: totals.items, points: number(totals.points) });
}

/** §9.3: "Not done (carried to Sprint 8)" / "(carried to the backlog)"; plain "Not done" while the sprint is open. */
export function notDoneLabel(facts: ReportSprintScope): string {
  if (!facts.carriedTo) return retrospectiveText("notDone");
  const target = facts.carriedTo === "backlog" ? retrospectiveText("theBacklog") : facts.carriedTo;
  return `${retrospectiveText("notDone")} (${retrospectiveText("carriedTo", { target })})`;
}

/** §9.3: the facts strip as label / value pairs, in the spec's order. */
export function factRows(facts: ReportSprintScope): { label: string; value: string }[] {
  return [
    { label: retrospectiveText("committed"), value: totalsText(facts.committedAtStart) },
    { label: retrospectiveText("added"), value: totalsText(facts.added) },
    { label: retrospectiveText("removed"), value: totalsText(facts.removed) },
    { label: retrospectiveText("completed"), value: totalsText(facts.completed) },
    { label: notDoneLabel(facts), value: totalsText(facts.remaining) },
  ];
}

/**
 * §9.9 "Copy as text": plain text for pasting into minutes — the heading, dates, goal, the facts and the three headed lists.
 * Removed entries are left out; an action that became an item names its key and owner.
 */
export function retrospectiveAsText(retro: SprintRetrospective): string {
  const sprint = retro.sprint;
  const lines = [retrospectiveText("title", { sprint: sprint.name })];
  if (sprint.startAt) lines.push(retrospectiveText("dates", { start: formatDate(sprint.startAt), end: formatDate(sprint.completedAt ?? sprint.endAt) }));
  if (sprint.goal) lines.push(retrospectiveText("goal", { goal: sprint.goal }));
  if (retro.facts) {
    lines.push("", retrospectiveText("factsTitle"));
    for (const row of factRows(retro.facts)) lines.push(`- ${row.label}: ${row.value}`);
  }
  for (const list of LISTS) {
    lines.push("", list.title());
    const entries = entriesOf(retro, list.kind).filter((entry) => entry.text !== null);
    if (entries.length === 0) lines.push(`- ${retrospectiveText("empty")}`);
    for (const entry of entries) {
      const item = entry.actionItem ? ` [${entry.actionItem.key}${entry.actionItem.owner ? `, ${entry.actionItem.owner.displayName}` : ""}]` : "";
      lines.push(`- ${(entry.text ?? "").replace(/\s*\n\s*/g, " ")} (${entry.author.displayName})${item}`);
    }
  }
  return lines.join("\n");
}

/** The ACTION's default due date: the sprint's planned end + 14 days, as the date input writes it; "" when there is no end. */
export function defaultDueDate(endAt: string | null | undefined): string {
  if (!endAt) return "";
  const end = new Date(endAt);
  const due = new Date(end.getFullYear(), end.getMonth(), end.getDate() + 14);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${due.getFullYear()}-${pad(due.getMonth() + 1)}-${pad(due.getDate())}`;
}

/**
 * Batch 19 (OBS-B18U-7): may a project member own a retrospective action? Only a member with a role other than the read-only
 * Viewer (Passenger · Viewer) and SystemAdmin, the same rule as the server (`RetrospectiveRules.MayOwnAction`).
 */
export function mayOwnAction(roles: readonly string[]): boolean {
  return roles.some((role) => role !== "Viewer" && role !== "SystemAdmin");
}
